import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import {
  fetchQuestions,
  answerQuestion,
  dismissQuestion,
  requestVoiceLink,
  proposeVolatility,
  QuestionsError,
  VOLATILITY_CHOICES,
  answeredDrawerLine,
  type Question,
  type QuestionStatus,
  type VoiceLinkResult,
  type VolatilityClass,
} from '@/lib/api'
import DrawerPicker from '@/components/DrawerPicker'
import { VoiceAnswerPanel } from '@/components/VoiceAnswer'
import { isChosenDrawer, type DrawerChoice } from '@/lib/drawer-choice'
import { withTranscript, afterDiscard, type VoiceNoteDraft } from '@/lib/voice-notes'
import type { Drawer, AnsweredFiling } from '@/lib/api'

/**
 * THE OWNER'S QUESTION QUEUE — shared by both owner surfaces (Kelvo build,
 * owner screen). Moved here WHOLE from src/routes/Dashboard.tsx, which is the
 * build standard's split-on-touch rule (§8.7) applied to the frontend: the
 * first time this group was needed outside the Foredesk dashboard, it moved
 * into its own module rather than being copied.
 *
 * WHY MOVED AND NOT COPIED. A second copy of the answer card is the frontend
 * version of the second answer pipeline answer.ts exists to prevent: the
 * volatility proposal, the certificate-date guard, the "shown, never applied
 * silently" rule — each would drift one fix at a time between Foredesk and
 * Kelvo, and the owner of either would never see the seam.
 *
 * BEHAVIOUR IS UNCHANGED FOR FOREDESK. The only differences are two props the
 * dashboard now passes explicitly: `voice` (the voice button, which only
 * Foredesk can serve) and the empty-state wording (customers vs staff). Every
 * other line is the code that was in Dashboard.tsx, in the same order.
 */

export function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={
        '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 ' +
        (active
          ? 'border-brand-primary text-text-primary'
          : 'border-transparent text-text-muted hover:text-text-primary')
      }
      aria-current={active ? 'page' : undefined}
    >
      {children}
    </button>
  )
}

export type LoadState = 'loading' | 'loaded' | 'error'

/**
 * Loads the signed-in supplier's questions for one status. getToken is read
 * through a ref so `load` depends only on `status` — avoiding a refetch loop if
 * Clerk hands back a fresh getToken identity on re-render.
 */
export function useQuestions(status: QuestionStatus) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [questions, setQuestions] = useState<Question[]>([])
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      const token = await getTokenRef.current()
      const list = await fetchQuestions(token, status)
      setQuestions(list)
      setState('loaded')
    } catch (e) {
      setError(e instanceof QuestionsError ? e.message : 'Could not load questions.')
      setState('error')
    }
  }, [status])

  useEffect(() => {
    void load()
  }, [load])

  return { questions, state, error, reload: load }
}

/**
 * The owner's pending queue.
 *
 * `voice` switches on the "Answer by voice" button. It needs the voice build's
 * secrets (Speechmatics, Twilio, the capture signing key), which only Foredesk
 * holds — Kelvo does not, and its voice endpoints fail closed with a 503. So a
 * surface without them does not offer the button at all, rather than offering
 * one that can only ever error.
 *
 * `emptyMessage` names who asks: customers on Foredesk, staff on Kelvo.
 *
 * `drawers` (Kelvo item 2) adds "Which drawer does this answer go in?" to every
 * card, and the answer will not save until one is chosen: the drawer decides
 * which staff are told it. Absent (Foredesk): no picker, the same request.
 *
 * `voiceNotes` (Build 10b, Kelvo) records on the page itself, on the owner's
 * session: the words come back as a draft in the answer box (src/components/
 * VoiceAnswer.tsx). Different from `voice`, which texts Foredesk's owner a
 * link; a card never offers both. Absent (Foredesk): no such button, and the
 * answer request is exactly what it always was.
 */
export function PendingPanel({
  voice = false,
  voiceNotes = false,
  drawers = false,
  emptyMessage,
}: {
  voice?: boolean
  voiceNotes?: boolean
  drawers?: boolean
  emptyMessage: string
}) {
  const { getToken } = useAuth()
  const { questions, state, error, reload } = useQuestions('pending')

  // Actions throw on failure so the card can surface a per-row error; on success
  // we reload the authoritative list rather than guessing the new UI state.
  async function handleAnswer(
    id: string,
    answer: string,
    volatilityClass: VolatilityClass,
    certExpiresAt?: number,
    drawer?: Drawer,
    voiceNoteId?: string,
  ) {
    const token = await getToken()
    await answerQuestion(token, id, answer, volatilityClass, certExpiresAt, drawer, voiceNoteId)
    await reload()
  }
  async function handleDismiss(id: string) {
    const token = await getToken()
    await dismissQuestion(token, id)
    await reload()
  }
  // DELIBERATELY DOES NOT RELOAD. Minting a voice link answers nothing: the
  // question stays pending until a recording is transcribed and approved, so
  // reloading here would unmount the card and take the link away with it.
  async function handleVoice(id: string) {
    const token = await getToken()
    return requestVoiceLink(token, id)
  }

  if (state === 'loading') return <PanelMessage>Loading questions…</PanelMessage>
  if (state === 'error') {
    return (
      <PanelMessage tone="danger">
        {error}{' '}
        <button onClick={() => void reload()} className="underline underline-offset-2">
          Try again
        </button>
      </PanelMessage>
    )
  }
  if (questions.length === 0) {
    return <PanelMessage>{emptyMessage}</PanelMessage>
  }

  return (
    <ul className="mt-5 stack-y-4">
      {questions.map((q) => (
        // Keyed on the draft too: a list reloaded with a draft this card has
        // not seen (made in another tab) remounts the card with it.
        <li key={`${q.id}:${q.voiceDraft?.id ?? ''}`}>
          <PendingCard
            q={q}
            onAnswer={handleAnswer}
            onDismiss={handleDismiss}
            onVoice={voice && !voiceNotes ? handleVoice : undefined}
            // Offered only where the engine says this pod keeps voice answers:
            // it sends `voiceDraft` (null or a draft) on those pods alone.
            voiceNotes={voiceNotes && q.voiceDraft !== undefined}
            onStale={() => void reload()}
            drawers={drawers}
          />
        </li>
      ))}
    </ul>
  )
}

function PendingCard({
  q,
  onAnswer,
  onDismiss,
  onVoice,
  voiceNotes,
  onStale,
  drawers,
}: {
  q: Question
  onAnswer: (
    id: string,
    answer: string,
    volatilityClass: VolatilityClass,
    certExpiresAt?: number,
    drawer?: Drawer,
    voiceNoteId?: string,
  ) => Promise<void>
  onDismiss: (id: string) => Promise<void>
  /** Absent means no voice button — see PendingPanel's `voice`. */
  onVoice?: (id: string) => Promise<VoiceLinkResult>
  /** See PendingPanel's `voiceNotes`. */
  voiceNotes: boolean
  /** The list is out of date: reload it. */
  onStale: () => void
  /** See PendingPanel's `drawers`. */
  drawers: boolean
}) {
  // A draft recorded before he left the page is still here when he comes
  // back, with its words already in the box.
  const [voiceDraft, setVoiceDraft] = useState<VoiceNoteDraft | null>(q.voiceDraft ?? null)
  const [text, setText] = useState(q.voiceDraft?.transcript ?? '')
  const [busy, setBusy] = useState<null | 'answer' | 'dismiss' | 'voice'>(null)
  const [voice, setVoice] = useState<VoiceLinkResult | null>(null)
  const [err, setErr] = useState('')
  // The owner's explicit choice, once he makes one. Null means "still showing
  // the proposal" — which is not the same as having no value, and the
  // distinction is what keeps the proposal honest.
  const [chosen, setChosen] = useState<VolatilityClass | null>(null)
  const [certDate, setCertDate] = useState('')
  const [drawer, setDrawer] = useState<DrawerChoice>('')

  // Proposed live from what he has typed so far, by the SAME rules the engine
  // applies. Recomputed as he types so the suggestion cannot describe an earlier
  // draft of the answer.
  const proposal = useMemo(() => proposeVolatility(text), [text])
  const volatilityClass = chosen ?? proposal.volatilityClass
  const certExpiresAt =
    volatilityClass === 'cert_tied' && certDate
      ? Math.floor(new Date(certDate + 'T00:00:00Z').getTime() / 1000)
      : undefined

  async function submitAnswer() {
    if (!text.trim()) {
      setErr('Please write an answer first.')
      return
    }
    // A certificate's own date IS its expiry — the engine cannot derive one, and
    // would reject this anyway. Caught here so the owner is told what is missing
    // rather than shown a generic save failure.
    if (volatilityClass === 'cert_tied' && !certExpiresAt) {
      setErr('Add the date this certificate runs out.')
      return
    }
    // Kelvo: who may be told this answer is the owner's decision, made here.
    if (drawers && !isChosenDrawer(drawer)) {
      setErr('Choose which drawer this answer goes in.')
      return
    }
    setBusy('answer')
    setErr('')
    try {
      await onAnswer(
        q.id,
        text.trim(),
        volatilityClass,
        certExpiresAt,
        drawers && isChosenDrawer(drawer) ? drawer : undefined,
        // His words go as the answer; the draft's id only keeps the recording
        // against it (Build 10b).
        voiceNotes && voiceDraft ? voiceDraft.id : undefined,
      )
      // On success the list reloads and this card unmounts — no reset needed.
    } catch (e) {
      setErr(e instanceof QuestionsError ? e.message : 'Could not save the answer.')
      setBusy(null)
    }
  }

  async function submitDismiss() {
    setBusy('dismiss')
    setErr('')
    try {
      await onDismiss(q.id)
    } catch (e) {
      setErr(e instanceof QuestionsError ? e.message : 'Could not dismiss the question.')
      setBusy(null)
    }
  }

  /**
   * ANSWER BY VOICE (Build 1) — mint a capture and text the owner the link.
   *
   * WHY THIS BUTTON EXISTS ONLY NOW. The endpoint, the record page, the
   * transcription and the review queue have all been live and tested since Build
   * 1. Nothing in this dashboard ever called them, so the feature the product
   * leads on \u2014 "you answer it once, out loud" \u2014 had no front door here, and
   * the Voice drafts tab invited the owner to review recordings he had no way to
   * make. Two suites green either side of a transition nothing asserted.
   *
   * THE CARD DOES NOT UNMOUNT ON SUCCESS, unlike answering. The question stays
   * pending until a recording is approved, which is correct: nothing has been
   * answered yet. So this sets its own result state rather than relying on the
   * list reloading, and the owner is told plainly whether the text went \u2014 an
   * SMS that silently failed would leave him waiting on a phone for a link that
   * is not coming.
   */
  async function submitVoice() {
    if (!onVoice) return
    setBusy('voice')
    setErr('')
    setVoice(null)
    try {
      setVoice(await onVoice(q.id))
    } catch (e) {
      setErr(e instanceof QuestionsError ? e.message : 'Could not create the link.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-raised p-5">
      <p className="text-xs text-text-muted">{formatTime(q.created_at)}</p>
      <p className="mt-1 text-text-primary">{q.question_text}</p>

      {voiceNotes ? (
        <VoiceAnswerPanel
          questionId={q.id}
          draft={voiceDraft}
          onDraft={(d) => {
            setVoiceDraft(d)
            setText((t) => withTranscript(t, d.transcript))
            setErr('')
          }}
          onDiscarded={() => {
            if (voiceDraft) setText((t) => afterDiscard(t, voiceDraft.transcript))
            setVoiceDraft(null)
          }}
          onStale={onStale}
          disabled={busy !== null}
        />
      ) : null}

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={busy !== null}
        rows={voiceNotes && voiceDraft ? 5 : 3}
        placeholder="Write your answer…"
        aria-label="Your answer"
        className="mt-3 w-full resize-y rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
      />

      {/*
        HOW OFTEN THIS CHANGES. Shown, never applied silently — the engine will
        propose a class if none is sent, but a proposal the owner never saw is a
        default wearing a friendlier name, and a price quietly classed as
        long-lived answers stale for a year with nothing to flag it.

        Only appears once he has written something: proposing a class for an
        empty box would be a guess about nothing.
      */}
      {text.trim() && (
        <div className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3">
          <label
            htmlFor={`volatility-${q.id}`}
            className="block text-xs font-medium text-text-primary"
          >
            How often does this change?
          </label>
          <p className="mt-1 text-xs text-text-muted">
            {chosen === null ? proposal.reason : 'You have set this yourself.'}
          </p>
          <select
            id={`volatility-${q.id}`}
            value={volatilityClass}
            onChange={(e) => setChosen(e.target.value as VolatilityClass)}
            disabled={busy !== null}
            className="mt-2 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {VOLATILITY_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>

          {volatilityClass === 'cert_tied' && (
            <div className="mt-2">
              <label
                htmlFor={`cert-${q.id}`}
                className="block text-xs font-medium text-text-primary"
              >
                When does the certificate run out?
              </label>
              <input
                id={`cert-${q.id}`}
                type="date"
                value={certDate}
                onChange={(e) => setCertDate(e.target.value)}
                disabled={busy !== null}
                className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-raised px-3 py-2 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
              />
            </div>
          )}
        </div>
      )}

      {drawers && text.trim() ? (
        <div className="mt-3">
          <DrawerPicker
            id={`drawer-${q.id}`}
            label="Which drawer does this answer go in?"
            hint="This decides which of your staff your assistant will tell it to."
            value={drawer}
            onChange={setDrawer}
            disabled={busy !== null}
          />
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-2">
        <button
          onClick={submitAnswer}
          disabled={busy !== null}
          className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {busy === 'answer' ? 'Saving…' : 'Answer'}
        </button>
        {onVoice && (
          <button
            onClick={submitVoice}
            disabled={busy !== null}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-primary transition-colors hover:border-brand-accent disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            {busy === 'voice' ? 'Sending\u2026' : 'Answer by voice'}
          </button>
        )}
        <button
          onClick={submitDismiss}
          disabled={busy !== null}
          className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-muted transition-colors hover:text-text-danger disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </button>
      </div>

      {/*
        THE LINK IS ALWAYS SHOWN, not only when the text fails. The owner may be
        at the desk he is reading this on, and making him wait for an SMS to
        reach a phone in another room would be theatre. The SMS is for the van;
        the link is for here.
      */}
      {voice && (
        <div className="mt-3 rounded-lg border border-border-subtle bg-surface-base p-3">
          <p className="text-sm text-text-primary">
            {voice.smsSent
              ? 'Link texted to your phone. Tap it, speak your answer, and it appears in Voice drafts to check.'
              : 'Could not text the link, so use this one. Speak your answer and it appears in Voice drafts to check.'}
          </p>
          <a
            href={`/record/${voice.token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block break-all text-sm font-medium text-brand-primary underline underline-offset-2 hover:no-underline"
          >
            Open the recorder
          </a>
        </div>
      )}

      {err && <p className="mt-2 text-sm text-text-danger">{err}</p>}
    </div>
  )
}

export function AnsweredPanel({ emptyMessage }: { emptyMessage: string }) {
  const { questions, state, error, reload } = useQuestions('answered')

  if (state === 'loading') return <PanelMessage>Loading answers…</PanelMessage>
  if (state === 'error') {
    return (
      <PanelMessage tone="danger">
        {error}{' '}
        <button onClick={() => void reload()} className="underline underline-offset-2">
          Try again
        </button>
      </PanelMessage>
    )
  }
  if (questions.length === 0) {
    return <PanelMessage>{emptyMessage}</PanelMessage>
  }

  return (
    <ul className="mt-5 stack-y-4">
      {questions.map((q) => (
        <li key={q.id} className="rounded-card border border-border-subtle bg-surface-raised p-5">
          <p className="text-xs text-text-muted">
            Answered {q.answered_at ? formatTime(q.answered_at) : ''}
          </p>
          <p className="mt-1 text-text-primary">{q.question_text}</p>
          <p className="mt-3 whitespace-pre-wrap rounded-lg bg-surface-base px-3 py-2 text-sm text-text-primary">
            {q.answer_text}
          </p>
          {/* Kelvo part 2c: who is told this answer now. Only when the engine
              sent `filed` — a drawers pod — so Foredesk's cards are unchanged
              and never link to a page Foredesk does not have. */}
          {q.filed !== undefined ? <AnsweredDrawer filed={q.filed} /> : null}
        </li>
      ))}
    </ul>
  )
}

/**
 * Where an answer is filed now, and the way to the fact itself on /knows,
 * where it can be moved or reworded. Unfiled and "nothing on record" are
 * flagged in red: an answer to a staff question that only the owner is told
 * is almost never what he meant.
 */
function AnsweredDrawer({ filed }: { filed: AnsweredFiling | null }) {
  const line = answeredDrawerLine(filed)
  return (
    <p className={`mt-2 text-xs ${line.flagged ? 'font-medium text-text-danger' : 'text-text-muted'}`}>
      {line.text}
      {filed !== null && filed.live ? (
        <>
          {' · '}
          <Link
            to={`/knows#fact-${encodeURIComponent(filed.factId)}`}
            className="font-normal text-text-primary underline underline-offset-2"
          >
            {filed.drawer === null ? 'Choose a drawer' : 'See it in Facts'}
          </Link>
        </>
      ) : null}
    </p>
  )
}

export function PanelMessage({
  children,
  tone = 'muted',
}: {
  children: ReactNode
  tone?: 'muted' | 'danger'
}) {
  return (
    <p className={'mt-6 text-sm ' + (tone === 'danger' ? 'text-text-danger' : 'text-text-muted')}>
      {children}
    </p>
  )
}

/** Format a unix-epoch-seconds timestamp for UK readers; empty string if unset. */
export function formatTime(epochSeconds: number): string {
  if (!epochSeconds) return ''
  return new Date(epochSeconds * 1000).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
